import { draftOrderStatusEnums } from '@/types/enums';
import z from 'zod';

export const post = {
  body: z.object({
    status: z.string().nonempty({ error: 'Status is required' }),
    title: z.string().nonempty({ error: 'Title is required' }),
    project: z.coerce.number().min(1, { error: 'Project id is required' }),
    supplier: z.coerce.number().min(1, { error: 'Supplier id is required' }),
    created_by: z.coerce.number().min(1, { error: 'User id is required' }),
    function_number: z.string().optional(),
  }),
  response: z.object({
    id: z.number(),
    lines: z.array(
      z.object({
        id: z.number(),
        created_at: z.date(),
        updated_at: z.date(),
        qty: z.number(),
        unit_price: z.string(),
        needed_by: z.date(),
        notes: z.string(),
        draft: 0,
        part: 0,
      }),
    ),
    created_at: z.date(),
    updated_at: z.date(),
    status: z.string(),
    title: z.string(),
    function_number: z.string().optional(),
    release_ids: z.string(),
    project: z.number(),
    supplier: z.number(),
    created_by: z.number(),
  }),
  params: z.never(),
  query: z.never(),
};
const postSchema = z.object(post);
export type Post = z.infer<typeof postSchema>;

const get = {
  body: z.never(),
  response: z.array(
    z.object({
      id: z.number(),
      release_numbers: z.string().array(),
      lines: z.array(
        z.object({
          id: z.number(),
          draft: z.number(),
          part_id: z.number(),
          part_number: z.string(),
          qty: z.number(),
          unit_price: z.string(),
          needed_by: z.string(),
          notes: z.string().optional(),
        }),
      ),
      created_at: z.date(),
      updated_at: z.date(),
      status: z.enum(draftOrderStatusEnums?.map((status) => status?.value)),
      title: z.string(),
      function_number: z.string(),
      release_ids: z.string(),
      project: z.number(),
      supplier: z.number(),
      created_by: z.number(),
    }),
  ),
  params: z.never(),
  query: z.never(),
};
const getSchema = z.object(get);
export type Get = z.infer<typeof getSchema>;

export const patch = {
  body: post.body.partial(),
  response: z.object({
    id: z.number(),
    lines: z.array(
      z.object({
        id: z.number(),
        created_at: z.date(),
        updated_at: z.date(),
        qty: z.number(),
        unit_price: z.string(),
        needed_by: z.date(),
        notes: z.string(),
        draft: 0,
        part: 0,
      }),
    ),
    created_at: z.date(),
    updated_at: z.date(),
    status: z.enum(draftOrderStatusEnums?.map((status) => status?.value)),
    title: z.string(),
    project: z.number(),
    supplier: z.number(),
    created_by: z.number(),
  }),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const patchSchema = z.object(patch);
export type Patch = z.infer<typeof patchSchema>;

const put = {
  body: z.object({
    status: z.string(),
    title: z.string(),
    project: z.number(),
    supplier: z.number(),
    created_by: z.number(),
  }),
  response: z.object({
    id: z.number(),
    lines: z.array(
      z.object({
        id: z.number(),
        created_at: z.date(),
        updated_at: z.date(),
        qty: z.number(),
        unit_price: z.string(),
        needed_by: z.date(),
        notes: z.string(),
        draft: z.number(),
        part: z.number(),
      }),
    ),
    created_at: z.date(),
    updated_at: z.date(),
    status: z.enum(draftOrderStatusEnums?.map((status) => status?.value)),
    title: z.string(),
    project: z.number(),
    supplier: z.number(),
    created_by: z.number(),
  }),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const putSchema = z.object(put);
export type Put = z.infer<typeof putSchema>;

const getDraftOrder = {
  body: z.never(),
  response: z.object({
    id: z.number(),
    release_numbers: z.string().array(),
    lines: z.array(
      z.object({
        id: z.number(),
        draft: z.number(),
        part_id: z.number(),
        part_number: z.string(),
        qty: z.number(),
        unit_price: z.string(),
        needed_by: z.string(),
        notes: z.string().optional(),
      }),
    ),
    created_at: z.date(),
    updated_at: z.date(),
    status: z.enum(draftOrderStatusEnums?.map((status) => status?.value)),
    title: z.string(),
    function_number: z.string(),
    release_ids: z.string(),
    project: z.number(),
    supplier: z.number(),
    created_by: z.number(),
  }),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const getDraftOrderSchema = z.object(getDraftOrder);
export type GetDraftOrder = z.infer<typeof getDraftOrderSchema>;
